/**
 * Validation table sub-components for ValidationPhase.
 * Split out to keep validation-phase.tsx under the max-lines limit.
 */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { ImportErrorRow } from '@ooh/contracts';
import { ExpandableTableRow } from '@ooh/ui/components/expandable-table-row';
import type { ExpandableTableCell } from '@ooh/ui/components/expandable-table-row';
import { RowPreviewPanel } from '@ooh/ui/components/row-preview-panel';
import type { RowPreviewColumn } from '@ooh/ui/components/row-preview-panel';

type ErrorPreviewRow = {
  row: string;
  nosnik: string;
  pole: string;
  komunikat: string;
};

const ERROR_COLUMNS: RowPreviewColumn<ErrorPreviewRow>[] = [
  { key: 'row', label: 'Wiersz' },
  { key: 'nosnik', label: 'Nośnik' },
  { key: 'pole', label: 'Pole' },
  { key: 'komunikat', label: 'Komunikat' },
];

const ISSUE_HIGHLIGHT_FIELDS = ['Pole'];

// resolveValidationMsg translates an error/autofix code to a localised string when
// a code is present; otherwise falls back to the pre-translated message from the server.
// The cast is required because i18next expects statically-known key literals; codes are
// dynamic (server-driven) and validated against locale keys at runtime.
export type TFn = (key: string, params?: Record<string, string>) => string;
export function resolveValidationMsg(
  t: TFn,
  code: string | undefined,
  message: string,
  params?: Record<string, string>,
): string {
  if (code) {
    return t(`validation.errors.${code}`, params ?? {});
  }
  return message;
}

function buildIssueCells(issue: ImportErrorRow, translatedMessage: string): ExpandableTableCell[] {
  return [
    {
      content: issue.row,
      className: 'px-4 py-2.5 font-mono text-xs text-muted-foreground w-[70px]',
    },
    {
      content: issue.grwId ?? '\u2014',
      className: 'px-4 py-2.5 font-mono text-xs text-foreground',
    },
    { content: issue.field, className: 'px-4 py-2.5 text-xs text-foreground' },
    { content: translatedMessage, className: 'px-4 py-2.5 text-muted-foreground' },
  ];
}

export function useExpandSet() {
  const [keys, setKeys] = React.useState<Set<string>>(new Set());

  function toggle(key: string) {
    setKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }
  function expandAll(all: string[]) {
    setKeys(new Set(all));
  }
  function collapseAll() {
    setKeys(new Set());
  }

  return { keys, toggle, expandAll, collapseAll };
}

type IssueSeverity = 'error' | 'warning';

function toSeverity(s: string | undefined): IssueSeverity {
  return s === 'error' ? 'error' : 'warning';
}

interface IssuesTableProps {
  errors: ImportErrorRow[];
}

export function IssuesTable({ errors }: IssuesTableProps) {
  const { t } = useTranslation('mediaImport');
  const { keys: expandedKeys, toggle, expandAll, collapseAll } = useExpandSet();

  const allIssueKeys = errors.map((issue) => `${issue.row}-${issue.field}`);
  const allExpanded = allIssueKeys.length > 0 && allIssueKeys.every((k) => expandedKeys.has(k));

  // colCount = 4 data cols + 1 toggle col
  const COL_COUNT = 5;

  return (
    <div className="rounded-md border border-border overflow-hidden" data-testid="validation-table">
      <div className="flex justify-end px-4 py-1.5 border-b border-border/50 bg-muted/10">
        <button
          type="button"
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          onClick={allExpanded ? collapseAll : () => expandAll(allIssueKeys)}
          data-testid="issues-expand-all"
        >
          {allExpanded ? t('validation.issuesCollapseAll') : t('validation.issuesExpandAll')}
        </button>
      </div>
      <table className="w-full text-[13px]">
        <thead>
          <tr className="border-b border-border bg-muted/30">
            <th className="text-left px-4 py-2.5 font-medium text-muted-foreground w-[70px]">
              {t('validation.tableHeaderRow')}
            </th>
            <th className="text-left px-4 py-2.5 font-medium text-muted-foreground w-[120px]">
              {t('validation.tableHeaderNosnik')}
            </th>
            <th className="text-left px-4 py-2.5 font-medium text-muted-foreground w-[140px]">
              {t('validation.tableHeaderField')}
            </th>
            <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">
              {t('validation.tableHeaderMessage')}
            </th>
          </tr>
        </thead>
        <tbody>
          {errors.map((issue, issueIdx) => {
            const rowKey = `${issue.row}-${issue.field}`;
            const isExpanded = expandedKeys.has(rowKey);
            // severity mapped outside JSX expression context to avoid i18next lint
            const severity = toSeverity(issue.severity);
            const msg = resolveValidationMsg(
              t as unknown as TFn,
              issue.code,
              issue.message,
              issue.params,
            );
            const cells = buildIssueCells(issue, msg);

            const previewRow: ErrorPreviewRow = {
              row: String(issue.row),
              nosnik: issue.grwId ?? '\u2014',
              pole: issue.field,
              komunikat: msg,
            };

            return (
              <ExpandableTableRow
                key={rowKey}
                variant={severity}
                expanded={isExpanded}
                onToggle={() => toggle(rowKey)}
                toggleAriaLabel={
                  isExpanded ? t('validation.collapseRow') : t('validation.expandRow')
                }
                toggleTestId={`row-expand-btn-${issue.row}`}
                colCount={COL_COUNT}
                isFirst={issueIdx === 0}
                // eslint-disable-next-line i18next/no-literal-string -- CSS utility class, not UI text
                hoverClassName="hover:bg-muted/20"
                testId={`validation-row-${issue.row}`}
                cells={cells}
              >
                <RowPreviewPanel<ErrorPreviewRow>
                  label={t('validation.rowDetailLabel', { row: issue.row })}
                  columns={ERROR_COLUMNS}
                  row={previewRow}
                  highlightFields={ISSUE_HIGHLIGHT_FIELDS}
                  highlightColor="issue"
                  issueSeverities={{ Pole: severity }}
                  colSpan={COL_COUNT}
                  emptyLabel={t('validation.empty')}
                  testId={`row-detail-panel-${issue.row}`}
                />
              </ExpandableTableRow>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
